/**
 * Class description
 *
 * Created: 2019-08-30
 * @author jonas
 */
import {TimelineLite, Timeline, Linear, RoughEase, TweenLite} from "gsap";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SASymbols, SpacierArcadeReelSymbol} from "../symbols/SpacierArcadeReelSymbol";
import {SpacierArcadeGameConfig} from "../SpacierArcadeGameConfig";
import {SpacierArcadeParsedGameData} from "../server/data/SpacierArcadeParsedGameData";
import {SpacierArcadeGameModuleConfig} from "../SpacierArcadeGameModuleConfig";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SAReelController, SAReelEvent} from "../reel/SAReelController";
import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SpacierArcade} from "../SpacierArcade";
import {SpacierArcadeWildSymbol} from "../symbols/SpacierArcadeWildSymbol";
import {SpacierArcadeGameMode} from "../gamemode/SpacierArcadeGameMode";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpacierArcadeSoundConfig} from "../SpacierArcadeSoundConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameRoundManager} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundManager";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";

export class ReelStopPresentations implements GameRoundPresentation {
    private readonly _reelId:number;
    private _stopDelay:number;
    private _quickStopDelay:number;
    stagger?:number;
    abortStagger?:number;
    private _tl:Timeline;
    protected _scatterPositions:number[];

    private _overlaySymsLength: any = {
        "NORMAL": 3,
        "WILD_RESPIN" : 3,
        "FREESPIN": 5,
        "FREESPIN_WIN_RESPIN": 5
    }

    constructor(reelId:number, stagger?:number, abortStagger?:number) {
        this._reelId = reelId;
        this._stopDelay = SpacierArcade.timelineReelsController.getReelConfig(this._reelId).stopDelay;
        this._quickStopDelay = SpacierArcade.timelineReelsController.getReelConfig(this._reelId).quickStopDelay;

        //Override config
        if(stagger != undefined) {
            this._stopDelay = stagger;
        }
        if(abortStagger != undefined) {
            this._quickStopDelay = abortStagger;
        }

        this.stagger = this._stopDelay;
        this.abortStagger = this._quickStopDelay;
    }

    public prepare(data:SpacierArcadeParsedGameData):void {
        this.stagger = data.mode === SpacierArcadeGameMode.FREESPIN_RESPIN? SpacierArcadeGameConfig.instance.FS_RESPIN_REEL_STOP_DELAY : this._stopDelay;
        this._scatterPositions = data.scatterPositions != null ? data.scatterPositions : ArrayHelper.initArrayWithValues(GameConfig.instance.REELS_NUM, () => -1);
        this.stagger = GameSetting.isFastSpin ? this._quickStopDelay : this._stopDelay;
        if(this.hasWildOnReel(data) && data.payoutMultiplier > 1 && this.isWinOrNextRespin(data) && (data.mode === SpacierArcadeGameMode.NORMAL || data.mode === SpacierArcadeGameMode.FREESPIN)){
            this.stagger += ((data.payoutMultiplier - 1) * 0.3) + 0.5;
        }
        if(data.nearWinReels[this._reelId]){
            this.stagger += GameSetting.isFastSpin ? 1.25 : 0.95;
        }
    }

    private hasWildOnReel(data:SpacierArcadeParsedGameData):boolean{
        return data.overlayWildsMultipliers[this._reelId].filter(m => m > 0).length > 0;
    }

    private animateWildMultiplier(data:SpacierArcadeParsedGameData):Timeline{
        const tl:TimelineLite = new TimelineLite();
        data.overlayWildsMultipliers[this._reelId].forEach((multi, index) => {
            if(multi > 1){
                let wildSymbol:SpacierArcadeWildSymbol = <SpacierArcadeWildSymbol>Reels.getSymbol(this._reelId, index);
                wildSymbol.finalMultiplier = multi;
                if(this.isWinOrNextRespin(data)) {
                    for (let i = 1; i <= multi; i++) {
                        if (i === multi) {
                            tl.add([
                                () => wildSymbol.multiplier = i,
                                () => {
                                    SlotGame.sound.playEffect("wildMultiX" + (i % 3 === 0 ? "3" : i));
                                    SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.WILD_MULTI_FINAL);
                                },
                                wildSymbol.changeState({
                                    state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.winMulti
                                })], i === 1 ? 0 : (i - 1) * 0.3);
                            tl.add(TweenLite.fromTo(StageManager.stage, 0.4, {x: -0.5, y: -0.5}, {
                                x: 0.5,
                                y: 0.5,
                                ease: new RoughEase({
                                    strength: 8,
                                    points: 10,
                                    template: Linear.easeNone,
                                    randomize: false
                                }),
                                clearProps: "x"
                            }), i === 1 ? 0 : (i - 1) * 0.3);
                        }
                        else {
                            tl.add([
                                () => wildSymbol.multiplier = i,
                                () => {
                                    if(i==1) {
                                        SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.WILD_LAND_BOUNCE);
                                    }
                                    SlotGame.sound.playEffect("wildMultiX" + (i % 3 === 0 ? "3" : i))
                                },
                                wildSymbol.changeState({
                                    state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.multiplier
                                })], i === 1 ? 0 : (i - 1) * 0.3);
                        }
                    }
                }else
                    {
                    tl.add([
                        () => wildSymbol.multiplier = multi,
                        () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.WILD_LAND_BOUNCE),
                        () => wildSymbol.changeState({
                        state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.attention
                     })]);
                }
            }
            else if(multi === 1){
                let wildSymbol:SpacierArcadeWildSymbol = <SpacierArcadeWildSymbol>Reels.getSymbol(this._reelId, index);
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.WILD_LAND_BOUNCE)
                tl.add([
                    wildSymbol.changeState({
                        state: data.singleWin > 0? SpacierArcadeGameConfig.instance.SYMBOL_STATES.attention : SpacierArcadeGameConfig.instance.SYMBOL_STATES.landDead
                    })
                ]);
            }
        });
        return tl;
    }

    private isOnlyScatterWin(data:SpacierArcadeParsedGameData):boolean{
        let scatterWin:boolean = true;
        for(let k:number = 0; k < data.betWins.length; k++) {
            let betWayWinData: any = <IBetWayWinData>data.betWins[k];
            if (betWayWinData.symbol !== SASymbols.S) {
                scatterWin = false;
                break;
            }
        }
        return scatterWin;
    }

    private isWinOrNextRespin(data:SpacierArcadeParsedGameData):boolean{
        return ((data.singleWin > 0 && !(this.isOnlyScatterWin(data) && data.mode === SpacierArcadeGameMode.NORMAL)) || data.nextMode === SpacierArcadeGameMode.RESPIN || data.nextMode === SpacierArcadeGameMode.FREESPIN_RESPIN);
    }

    public getAnimation(data:SpacierArcadeParsedGameData):Timeline | null {
        let saReelController:SAReelController = <SAReelController>Reels.getReel(this._reelId);
        saReelController.updateGameData(data);
        const tl:TimelineLite = SpacierArcade.timelineReelsController.stopSpin(this._reelId, data, false);
        const timeDelta:number = tl.totalDuration();
        if(data.nearWinReels[this._reelId]) {
            tl.add(() => {
                this.getBounceAnimation(data, GameSetting.isFastSpin ? 0.05 : 0);
            }, timeDelta + (GameSetting.isFastSpin ? 1.20 : 0.85));

            tl.add(() => {
                SpacierArcadeGameModuleConfig.saScatterAttentionView.stopNearWin(this._reelId);
                EventHandler.dispatchEvent(new GameEvent(SAReelEvent.RESET_NEAR_REEL_VIEW));
                Reels.allSymbols(0).forEach((sym: StateReelSymbol | null) => {
                    if (sym) {
                        sym.changeState({
                            state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.normal
                        })
                    }
                });
            }, GameSetting.isFastSpin ? "+=0.2": "+=0.3");
        }
        else {
            tl.add([
                this.getBounceAnimation(data, 0)
            ], GameSetting.isFastSpin ? "-=0.2" : "-=0.55");
        }
        const bounceTiming = 0;
        const bounceTime = tl.duration() + bounceTiming;
        if(!data.nearWinReels[this._reelId] && !this.isReelFullStacked(data) && ((!this.isRowCovered(data) && !GameSetting.isFastSpin) || (GameSetting.isFastSpin && this._reelId === 1 || this._reelId === 2  || this._reelId === 4))) {
            tl.add(() => {
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.REELS_DEFAULT_BOUNCE[this._reelId]);
            }, bounceTime - 0.25);
        }
        if(data.mode === SpacierArcadeGameMode.FREESPIN_RESPIN){
            let existingOverlaySyms:string[] = this.getDistinctArrayItems(data.overlaySymbolsBeforeSpin[this._reelId]);
            let newOverLays:string[] = this.getDistinctArrayItems(data.newOverlaySymbols[this._reelId]);
            let hasNewOverlays:boolean = data.newOverlaySymbols[this._reelId].filter((sym) => { return sym !== null }).length > 0;
            let hasNewOldOverlays:boolean = newOverLays.filter((sym) => { return existingOverlaySyms.indexOf(sym) > -1 && sym !== null }).length > 0;
            if(hasNewOldOverlays || (hasNewOverlays && this._reelId > 0)) {
                tl.add(() => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.FS_RESPIN_HIT), bounceTime - 0.25);
            }
        }
        if(this.hasWildOnReel(data) && (data.mode === SpacierArcadeGameMode.NORMAL || data.mode === SpacierArcadeGameMode.FREESPIN)){
            tl.add( () => {this.animateWildMultiplier(data)}, data.nearWinReels[this._reelId]? timeDelta + (GameSetting.isFastSpin ? 1.20 : 0.85) + 0.1 : 0.5);
        }
        this._tl = tl;
        return this._tl;
    }

    private isReelFullStacked(data:SpacierArcadeParsedGameData):boolean{
        let overlaySymbolsBeforeSpin:string[] = data.overlaySymbolsBeforeSpin[this._reelId];
        if(overlaySymbolsBeforeSpin.filter( (item) => { return item !== null }).length >= 5){
            return true;
        }
        return false;
    }

    private getBounceAnimation(data:SpacierArcadeParsedGameData, timeDelta:number = 0, isAbort:boolean = false):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            if(data.nearWinReels[this._reelId]) {
                SlotGame.sound.stopEffect(SpacierArcadeSoundConfig.instance.ANTICIPATION_SCATTER);
                if(data.mode === SpacierArcadeGameMode.NORMAL && data.nextMode !== SpacierArcadeGameMode.FREESPIN){
                    SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.ANTICIPATION_FAIL_SCATTER);
                }
            }
            if(this._scatterPositions[this._reelId] > -1){
                this.scatterSound(data);
            }
        }, (GameSetting.isFastSpin || isAbort)? timeDelta : 0.25);
        tl.add(() => {
            let reelSymbols:(SpacierArcadeReelSymbol | null)[] = <(SpacierArcadeReelSymbol | null)[]>Reels.allSymbols(this._reelId);
            reelSymbols.forEach((symbol) => {
                    if (symbol && (symbol.symName === SASymbols.S)) {
                        symbol.changeState({
                            state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.attention
                        });
                    }
            });
        }, timeDelta);
        return tl;
    }


    private resetExcitment():void{
        [0, 1].forEach((reelId: number) => {
            let reelSymbols: (StateReelSymbol | null)[] = Reels.allSymbols(reelId);
            reelSymbols.forEach((sym: StateReelSymbol | null) => {
                if (sym && sym.stackedSymName !== null && sym.symName !== SASymbols.W && sym.symName !== SASymbols.S) {
                    sym.changeState({
                        state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.normal
                    })
                }
            });
        });
    }

    public abort(isActive:boolean, data:SpacierArcadeParsedGameData):Timeline | null {
        if(isActive && this._tl) {
            GameRoundManager.detachAndPlayTimeline(this._tl);
            //Creating a new timeline with the same length as the time left on the active one, to make sure that the master timeline does not end before this one.
            const newTl = new TimelineLite();
            newTl.add(() => {
                let reelSymbols: (SpacierArcadeReelSymbol | null)[] = <(SpacierArcadeReelSymbol | null)[]>Reels.allSymbols(3);
                reelSymbols.forEach((symbol) => {
                    if (symbol && (symbol.symName === SASymbols.S)) {
                        symbol.changeState({
                            state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.attention
                        });
                    }
                });
            });
            newTl.add(new TweenLite(this, this._tl.duration() - this._tl.time(), {}));
            return newTl;
        }
        const tl: TimelineLite = SpacierArcade.timelineReelsController.stopSpin(this._reelId, data, true);

        if(data.nearWinReels[this._reelId]) {
            tl.add(() => {
                this.getBounceAnimation(data, 0, true)
            }, "-=0.25");
            tl.add(() => {
                EventHandler.dispatchEvent(new GameEvent(SAReelEvent.RESET_NEAR_REEL_VIEW));
                Reels.allSymbols(0).forEach((sym: StateReelSymbol | null) => {
                    if (sym) {
                        sym.changeState({
                            state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.normal
                        })
                    }
                });
                SpacierArcadeGameModuleConfig.saScatterAttentionView.stopNearWin(this._reelId);
            });
        }
        else{
            tl.add([
                this.getBounceAnimation(data, 0, true)
            ],"-=0.25");
        }
        if(!this.isReelFullStacked(data) && (this._reelId === 1 || this._reelId === 2  || this._reelId === 4)) {
            tl.add(() => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.REELS_DEFAULT_BOUNCE[this._reelId]), "-=0.25");
        }
        if(data.mode === SpacierArcadeGameMode.FREESPIN_RESPIN){
            let existingOverlaySyms:string[] = this.getDistinctArrayItems(data.overlaySymbolsBeforeSpin[this._reelId]);
            let newOverLays:string[] = this.getDistinctArrayItems(data.newOverlaySymbols[this._reelId]);
            let hasNewOverlays:boolean = data.newOverlaySymbols[this._reelId].filter((sym) => { return sym !== null }).length > 0;
            let hasNewOldOverlays:boolean = newOverLays.filter((sym) => { return existingOverlaySyms.indexOf(sym) > -1 && sym !== null }).length > 0;
            if(hasNewOldOverlays || (hasNewOverlays && this._reelId > 0)) {
                tl.add(() => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.FS_RESPIN_HIT), "-=0.25");
            }
        }
        if(this.hasWildOnReel(data) && (data.mode === SpacierArcadeGameMode.NORMAL || data.mode === SpacierArcadeGameMode.FREESPIN)){
            tl.add( () => {this.animateWildMultiplier(data)}, 0.5);
        }
        return tl;
    }

    private getDistinctArrayItems(overlays:string[]):string[]{
        const unique = (value:string, index:number, self:string[]) => {
            return self.indexOf(value) === index;
        }

        return overlays.filter(unique);
    }

    private isRowCovered(data:SpacierArcadeParsedGameData):boolean{
        if(data.mode === SpacierArcadeGameMode.FREESPIN_RESPIN || data.nextMode === SpacierArcadeGameMode.FREESPIN_RESPIN){
            const overlaySyms:string[] = data.overlaySymbolsBeforeSpin[this._reelId];
            if(overlaySyms.filter( (item) => { return item !== null }).length >= this._overlaySymsLength[data.mode]){
                return true;
            }
        }
        return false;
    }

    private scatterSound(data:SpacierArcadeParsedGameData):void{
        let hasScatter:boolean = this._scatterPositions[this._reelId] > -1;
        switch (hasScatter) {
            case this._reelId === 1:
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.SCATTER_1);
                break;
            case this._reelId === 2 && this._scatterPositions[1] > -1:
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.SCATTER_2);
                break;
            case this._reelId === 2 && this._scatterPositions[1] === -1:
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.SCATTER_1);
                break;
            case this._reelId === 3 && ((this._scatterPositions[1] === -1 && this._scatterPositions[2] > -1) || (this._scatterPositions[1] > -1 && this._scatterPositions[2] === -1)):
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.SCATTER_1);
                break;
            case this._reelId === 3 && (this._scatterPositions[1] > -1 && this._scatterPositions[2] > -1):
                SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.SCATTER_3);
                break;
        }
    }

}